import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { PasswordRequirementsChecklist } from '@/components/auth';
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_POLICY,
  passwordMeetsPolicy,
} from '@/lib/passwordPolicy';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const userId = params.get('uid') || '';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);

    if (password !== confirmPassword) {
      setMsg('Passwords do not match');
      return;
    }

    if (!passwordMeetsPolicy(password)) {
      setMsg(PASSWORD_POLICY.regex.description);
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch('/api/auth/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, userId, password }),
        credentials: 'include',
      });

      if (res.ok) {
        setIsSuccess(true);
        setMsg('Password updated successfully!');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        const data = await res.json().catch(() => ({}));
        setMsg(data.error || 'Reset failed. Link may be invalid or expired.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  if (!token || !userId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
        <Card className="w-full max-w-sm">
          <CardContent className="pt-6 text-center">
            <p className="text-destructive mb-4">Invalid reset link</p>
            <Link to="/forgot" className="text-sm text-primary hover:underline">
              Request a new reset link
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <Link to="/login" className="inline-block mb-4">
            <img src="/express_b.svg" alt="Bright.Express" className="h-10 w-10 mx-auto" />
          </Link>
          <h1 className="text-xl font-semibold">Reset password</h1>
          <p className="text-sm text-muted-foreground">Enter your new password</p>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="password">New password</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading || isSuccess}
                minLength={PASSWORD_MIN_LENGTH}
                autoComplete="new-password"
                revealLabel="new password"
              />
              <PasswordRequirementsChecklist
                password={password}
                isVisible={password.length > 0}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm password</Label>
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading || isSuccess}
                minLength={PASSWORD_MIN_LENGTH}
                autoComplete="new-password"
                revealLabel="confirm password"
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || isSuccess || !passwordMeetsPolicy(password)}
            >
              {isLoading ? 'Updating...' : 'Update password'}
            </Button>
          </form>
          {msg && (
            <p className={`text-sm text-center mt-4 ${isSuccess ? 'text-green-600' : 'text-destructive'}`}>
              {msg}
            </p>
          )}
          <div className="mt-4 text-center">
            <Link to="/login" className="text-sm text-primary hover:underline">
              Back to login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
